// Prozeduraler Textur-Atlas: 8 × 8 Kacheln zu je 16 × 16 Pixeln, gemalt aus einfachen Mustern
// mit leichtem Rauschen aus einem deterministischen Hash (kein Math.random), damit jeder Start
// denselben Atlas ergibt. `paintAtlas` ist rein und in Vitest prüfbar; `createAtlas` lädt die
// Pixel in ein Canvas und macht daraus eine Three.js-Textur mit NearestFilter ohne Mipmaps.
//
// Jede Blockseite (top/side/bottom) zeigt auf eine Kachel; die Rechtecke sind um einen halben
// Pixel nach innen gerückt, damit beim Abtasten nichts aus der Nachbarkachel blutet (ADR 0010).

import { CanvasTexture, NearestFilter, SRGBColorSpace } from 'three';
import { BLOCK, blockDef, type BlockId, type BlockName } from '@bauernhof/shared';
import type { AtlasLayout, BlockFace, TileRect } from './mesher.js';

/** Kantenlänge einer Kachel in Pixeln. */
export const TILE_SIZE = 16;
/** Kacheln je Zeile und Spalte des Atlas. */
export const ATLAS_TILES = 8;

type Rgb = [number, number, number];

/** Rauschen in [0, 1) für ein Pixel; `salz` trennt mehrere Werte je Pixel. */
type Rauschen = (salz: number) => number;

/** Malt ein Pixel (x, y in 0..15) einer Kachel. */
type Maler = (x: number, y: number, r: Rauschen) => Rgb;

/** Ganzzahl-Hash aus drei Werten, gleichmäßig auf [0, 1) verteilt. */
function hash(x: number, y: number, salz: number): number {
  let h = (Math.imul(x, 374761393) + Math.imul(y, 668265263) + Math.imul(salz, 2246822519)) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

/** Farbe um `staerke` heller oder dunkler, je nach Rauschwert in [0, 1). */
function rauh(basis: Rgb, wert: number, staerke: number): Rgb {
  const d = (wert - 0.5) * 2 * staerke;
  return [basis[0] + d, basis[1] + d, basis[2] + d];
}

const GRAS: Rgb = [92, 148, 52];
const ERDE: Rgb = [122, 86, 56];
const STEIN: Rgb = [130, 130, 128];
const MOERTEL: Rgb = [72, 68, 62];
const DUNKELHOLZ: Rgb = [70, 46, 26];
const BRETT: Rgb = [176, 128, 74];
const FUGE: Rgb = [104, 70, 40];

const malGras: Maler = (_x, _y, r) => rauh(GRAS, r(1), 18);
const malErde: Maler = (_x, _y, r) => (r(2) > 0.85 ? rauh([92, 62, 40], r(1), 10) : rauh(ERDE, r(1), 18));

/** Die Kacheln in Atlas-Reihenfolge; der Index ist der Platz im Raster. */
const KACHELN = {
  fehlt: (x, y) => ((x >> 2) + (y >> 2)) % 2 === 0 ? [255, 0, 255] : [0, 0, 0],
  gras_oben: malGras,
  gras_seite: (x, y, r) => (y < 3 || (y === 3 && r(3) > 0.5) ? malGras(x, y, r) : malErde(x, y, r)),
  erde: malErde,
  stein: (_x, _y, r) => (r(2) > 0.9 ? rauh([96, 96, 94], r(1), 8) : rauh(STEIN, r(1), 16)),
  pflaster: (x, y, r) => {
    const reihe = y >> 2;
    const versatz = (reihe % 2) * 2;
    const xs = (x + versatz) % 4;
    if (xs === 0 || y % 4 === 0) return rauh(MOERTEL, r(1), 6);
    const stein = hash((x + versatz) >> 2, reihe, 7);
    return rauh([148 + stein * 24, 140 + stein * 20, 128 + stein * 16], r(1), 10);
  },
  balken: (x, _y, r) => rauh(DUNKELHOLZ, hash(x, 0, 11) * 0.6 + r(1) * 0.4, 16),
  putz: (_x, _y, r) => rauh([232, 222, 200], r(1), 8),
  dachziegel: (x, y, r) => {
    const reihe = y >> 2;
    const versatz = (reihe % 2) * 2;
    if (y % 4 === 0 || (x + versatz) % 4 === 0) return rauh([94, 42, 28], r(1), 8);
    return rauh([156, 68, 44], hash((x + versatz) >> 2, reihe, 5) * 0.5 + r(1) * 0.5, 14);
  },
  holz: (x, y, r) => (y % 4 === 3 ? rauh(FUGE, r(1), 6) : rauh(BRETT, hash(0, y >> 2, 13) * 0.5 + r(1) * 0.5, 14 + (x % 5 === 0 ? 6 : 0))),
  laub: (_x, _y, r) => (r(2) > 0.8 ? rauh([30, 66, 26], r(1), 10) : rauh([46, 100, 38], r(1), 22)),
  stamm_seite: (x, _y, r) => rauh([88, 60, 38], hash(x, 0, 17) * 0.7 + r(1) * 0.3, 22),
  stamm_ende: (x, y, r) => {
    const abstand = Math.hypot(x - 7.5, y - 7.5);
    if (abstand > 7.5) return rauh([88, 60, 38], r(1), 10);
    return Math.floor(abstand) % 2 === 0 ? rauh([200, 164, 112], r(1), 8) : rauh([164, 124, 80], r(1), 8);
  },
  zaun: (x, y, r) => (y === 4 || y === 11 ? rauh([110, 84, 52], r(1), 6) : rauh([164, 134, 92], hash(0, y, 19) * 0.5 + r(1) * 0.5, 12 + (x % 7 === 0 ? 8 : 0))),
  pfosten: (x, _y, r) => rauh([104, 72, 44], hash(x, 0, 23) * 0.6 + r(1) * 0.4, 18),
  wasser: (x, y, r) => {
    const welle = (x + y * 2) % 7 === 0 ? 24 : 0;
    return rauh([54, 112, 194 + welle * 0.5], r(1), 10 + welle);
  },
  stroh: (x, y, r) => ((x + y) % 3 === 0 ? rauh([176, 138, 52], r(1), 10) : rauh([216, 182, 76], r(1), 16)),
  fenster: (x, y, r) => {
    const rahmen = x === 0 || x === 15 || y === 0 || y === 15;
    const kreuz = x === 7 || x === 8 || y === 7 || y === 8;
    if (rahmen || kreuz) return rauh([62, 42, 26], r(1), 6);
    return rauh([150, 182, 212], r(1), 10);
  },
  tuer: (x, y, r) => {
    if (y === 0) return rauh([60, 38, 22], r(1), 4);
    if (x === 11 && (y === 8 || y === 9)) return [208, 184, 88];
    if (x % 4 === 3) return rauh([70, 44, 24], r(1), 6);
    return rauh([116, 74, 40], hash(x >> 2, 0, 29) * 0.5 + r(1) * 0.5, 12);
  },
  // Ab hier die Kacheln der Blöcke aus Paket 2 (docs/architektur-paket-2.md, „Welt mit Parzellen“).
  // Das offene Tor bekommt keine eigene Kachel: Es wird gar nicht gezeichnet (ADR 0022).
  /** Tor: dasselbe Holz wie der Zaun, aber mit zwei dunklen Beschlagbändern statt Querlatten. */
  tor: (x, y, r) =>
    y === 2 || y === 13
      ? rauh([76, 56, 34], r(1), 6)
      : rauh([150, 116, 74], hash(0, y, 31) * 0.5 + r(1) * 0.5, 12 + (x % 6 === 0 ? 8 : 0)),
  /** Schild: helles, glattes Holz, damit der aufgemalte Name gut darauf steht. */
  schild: (x, _y, r) => rauh([198, 166, 116], hash(x, 0, 37) * 0.6 + r(1) * 0.4, 12),
  /** Kiste, Seite: Bretter mit dunklem Beschlag ringsum und einem Messingschloss vorn. */
  kiste: (x, y, r) => {
    if (x >= 7 && x <= 8 && y >= 6 && y <= 9) return rauh([214, 180, 80], r(1), 8);
    if (x === 0 || x === 15 || y === 0 || y === 15) return rauh([58, 46, 36], r(1), 6);
    return y % 5 === 4 ? rauh(FUGE, r(1), 6) : rauh([150, 104, 58], hash(0, y, 41) * 0.5 + r(1) * 0.5, 12);
  },
  /** Kiste, Deckel: dieselben Bretter, Beschlag quer, kein Schloss. */
  kiste_deckel: (x, y, r) => {
    if (y === 0 || y === 15 || x === 3 || x === 12) return rauh([58, 46, 36], r(1), 6);
    return x % 5 === 4 ? rauh(FUGE, r(1), 6) : rauh([150, 104, 58], hash(x, 0, 43) * 0.5 + r(1) * 0.5, 12);
  },
  /** Acker: gepflügte Erde mit dunklen Furchen quer über die Kachel. */
  acker: (x, y, r) =>
    x % 4 === 0 || (x % 4 === 1 && r(4) > 0.6)
      ? rauh([60, 42, 26], r(1), 8)
      : rauh([104, 74, 48], r(1), 16),
  /** Apfel: rot, mit kurzem Stiel oben. */
  apfel: (x, y, r) =>
    x >= 7 && x <= 8 && y < 3 ? rauh([70, 46, 26], r(1), 6) : rauh([198, 46, 42], r(1), 16),
} satisfies Record<string, Maler>;

type KachelName = keyof typeof KACHELN;
const KACHEL_NAMEN = Object.keys(KACHELN) as KachelName[];

/** Welche Kachel jede Blockseite bekommt. `luft` steht nur der Vollständigkeit halber dabei. */
const SEITEN: Record<BlockName, Record<BlockFace, KachelName>> = {
  luft: { top: 'fehlt', side: 'fehlt', bottom: 'fehlt' },
  gras: { top: 'gras_oben', side: 'gras_seite', bottom: 'erde' },
  erde: { top: 'erde', side: 'erde', bottom: 'erde' },
  stein: { top: 'stein', side: 'stein', bottom: 'stein' },
  pflaster: { top: 'pflaster', side: 'pflaster', bottom: 'pflaster' },
  balken: { top: 'balken', side: 'balken', bottom: 'balken' },
  putz: { top: 'putz', side: 'putz', bottom: 'putz' },
  dachziegel: { top: 'dachziegel', side: 'dachziegel', bottom: 'dachziegel' },
  holz: { top: 'holz', side: 'holz', bottom: 'holz' },
  laub: { top: 'laub', side: 'laub', bottom: 'laub' },
  stamm: { top: 'stamm_ende', side: 'stamm_seite', bottom: 'stamm_ende' },
  zaun: { top: 'zaun', side: 'zaun', bottom: 'zaun' },
  pfosten: { top: 'pfosten', side: 'pfosten', bottom: 'pfosten' },
  wasser: { top: 'wasser', side: 'wasser', bottom: 'wasser' },
  stroh: { top: 'stroh', side: 'stroh', bottom: 'stroh' },
  fenster: { top: 'fenster', side: 'fenster', bottom: 'fenster' },
  tuer: { top: 'tuer', side: 'tuer', bottom: 'tuer' },
  tor: { top: 'tor', side: 'tor', bottom: 'tor' },
  // Das offene Tor wird nie gezeichnet (ADR 0022); der Eintrag hält die Tabelle nur vollständig.
  tor_offen: { top: 'tor', side: 'tor', bottom: 'tor' },
  schild: { top: 'schild', side: 'schild', bottom: 'schild' },
  kiste: { top: 'kiste_deckel', side: 'kiste', bottom: 'kiste_deckel' },
  // Nur die Oberfläche ist gepflügt; von der Seite sieht man die Erde darunter.
  acker: { top: 'acker', side: 'erde', bottom: 'erde' },
  apfel: { top: 'apfel', side: 'apfel', bottom: 'apfel' },
};

/** Die Pixel des Atlas (RGBA, zeilenweise von oben) samt Layout. */
export interface AtlasPixels {
  /** Kantenlänge in Pixeln. */
  size: number;
  data: Uint8ClampedArray<ArrayBuffer>;
  layout: AtlasLayout;
}

/** Malt den Atlas; deterministisch, ohne DOM. Freie Plätze zeigen die Fehlkachel, nichts bleibt durchsichtig. */
export function paintAtlas(): AtlasPixels {
  const size = ATLAS_TILES * TILE_SIZE;
  const plaetze = ATLAS_TILES * ATLAS_TILES;
  if (KACHEL_NAMEN.length > plaetze) {
    throw new Error(`Atlas hat Platz für ${plaetze} Kacheln, gebraucht werden ${KACHEL_NAMEN.length}.`);
  }
  const data = new Uint8ClampedArray(new ArrayBuffer(size * size * 4));
  for (let platz = 0; platz < plaetze; platz++) {
    const maler: Maler = KACHELN[KACHEL_NAMEN[platz] ?? 'fehlt'];
    const x0 = (platz % ATLAS_TILES) * TILE_SIZE;
    const y0 = Math.floor(platz / ATLAS_TILES) * TILE_SIZE;
    for (let y = 0; y < TILE_SIZE; y++) {
      for (let x = 0; x < TILE_SIZE; x++) {
        const rgb = maler(x, y, (salz) => hash(x0 + x, y0 + y, salz));
        const i = ((y0 + y) * size + x0 + x) * 4;
        data[i] = rgb[0];
        data[i + 1] = rgb[1];
        data[i + 2] = rgb[2];
        data[i + 3] = 255;
      }
    }
  }
  return { size, data, layout: { tile: kachelRechteck } };
}

/**
 * Kachel-Rechteck einer Blockseite in Texturkoordinaten; v0 liegt unten, weil Three.js
 * Canvas-Texturen beim Hochladen spiegelt (flipY). Unbekannte ids zeigen die Fehlkachel.
 */
function kachelRechteck(id: BlockId, face: BlockFace): TileRect {
  // Unbekannte ids gelten laut Palette als Luft und landen so ebenfalls auf der Fehlkachel.
  const def = blockDef(id);
  const name = def.id === BLOCK.luft ? 'fehlt' : SEITEN[def.name][face];
  const platz = KACHEL_NAMEN.indexOf(name);
  const spalte = platz % ATLAS_TILES;
  const zeile = Math.floor(platz / ATLAS_TILES);
  const kachel = 1 / ATLAS_TILES;
  const rand = 0.5 / (ATLAS_TILES * TILE_SIZE);
  return [
    spalte * kachel + rand,
    1 - (zeile + 1) * kachel + rand,
    (spalte + 1) * kachel - rand,
    1 - zeile * kachel - rand,
  ];
}

/** Textur und Layout für das Blockmaterial. */
export interface Atlas {
  texture: CanvasTexture;
  layout: AtlasLayout;
}

/** Lädt den gemalten Atlas in ein Canvas und macht eine Textur ohne Filterung daraus. */
export function createAtlas(): Atlas {
  const pixel = paintAtlas();
  const canvas = document.createElement('canvas');
  canvas.width = pixel.size;
  canvas.height = pixel.size;
  const kontext = canvas.getContext('2d');
  if (kontext === null) throw new Error('Kein 2D-Kontext für den Textur-Atlas.');
  kontext.putImageData(new ImageData(pixel.data, pixel.size, pixel.size), 0, 0);

  const texture = new CanvasTexture(canvas);
  texture.magFilter = NearestFilter;
  texture.minFilter = NearestFilter;
  texture.generateMipmaps = false;
  texture.colorSpace = SRGBColorSpace;
  texture.needsUpdate = true;
  return { texture, layout: pixel.layout };
}
